import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Navigate, useNavigate } from "react-router-dom";

import { UserRole } from "@/types/users";
import { getUserRole } from "@/utils/users";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
  const { user, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  // Get user role
  const userRole = getUserRole(user);

  // If no user role, redirect to login and don't render anything
  useEffect(() => {
    if (userRole === null) {
      loginWithRedirect();
    }
  }, [userRole, loginWithRedirect]);

  // Don't render anything if user role is null
  if (userRole === null) {
    return null;
  }

  // If no specific roles are required, allow access
  if (!allowedRoles || allowedRoles.length === 0) {
    return <>{children}</>;
  }

  // Check if user has any of the allowed roles
  if (!allowedRoles.includes(userRole)) {
    // Redirect to unsupported role page if user doesn't have required role
    return <Navigate to="/unsupported-role" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
